import { useLocation } from "react-router-dom";
import noop from "lodash/noop";
import { leaves, routes, RoutesKeys } from "~/constants";
import { useAppSelector } from "~/store/hooks";
import { useOnPageLoad } from "./useOnPageLoad";

type Importer = () => Promise<unknown>;

const faqsPage: Importer = () => import("~/LandingApp/pages/FaqsPage");
const forgotPassword: Importer = () =>
  import("~/LandingApp/pages/ForgotPasswordPage");
const libraryPage: Importer = () => import("~/LandingApp/pages/LibraryPage");
const articlePage: Importer = () => import("~/LandingApp/pages/ArticlePage");
const authorPage: Importer = () => import("~/LandingApp/pages/AuthorPage");
const medicalReviewerPage: Importer = () =>
  import("~/LandingApp/pages/MedicalReviewerPage");
const topicPage: Importer = () => import("~/LandingApp/pages/TopicPage");
const resetPassword: Importer = () =>
  import("~/LandingApp/pages/ResetPasswordPage");
const signInPage: Importer = () => import("~/LandingApp/pages/SignInPage");
const signUpPage: Importer = () => import("~/LandingApp/pages/SignUpPage");

const allSetPage: Importer = () => import("~/MemberApp/pages/AllSetPage");
const crewPage: Importer = () => import("~/MemberApp/pages/CrewPage");
const dashboardPage: Importer = () => import("~/MemberApp/pages/DashboardPage");
const setPasswordPage: Importer = () =>
  import("~/MemberApp/pages/SetPasswordPage");
const settingsPage: Importer = () => import("~/MemberApp/pages/SettingsPage");
const referAFriendPage: Importer = () =>
  import("~/MemberApp/pages/ReferAFriendPage");

const initImport = (importer: Importer) => importer();

type Routes = (typeof routes)[RoutesKeys];
type ImportersMap = Record<Routes, Importer[]>;

// This map is a best-guess of where a User/Visitor would navigate to next
// after landing on the route.
const importersMap: ImportersMap = {
  [routes.about]: [],
  [routes.allSet]: [crewPage, dashboardPage, libraryPage, settingsPage],
  [routes.article]: [],
  [routes.author]: [],
  [routes.cancellationReason]: [],
  [routes.careers]: [],
  [routes.clinics]: [],
  [routes.confirmation]: [setPasswordPage, signInPage],
  [routes.crew]: [faqsPage, libraryPage, settingsPage, signInPage],
  [routes.dashboard]: [
    libraryPage,
    crewPage,
    authorPage,
    medicalReviewerPage,
    articlePage,
    settingsPage,
    signInPage,
    faqsPage,
    topicPage,
    referAFriendPage,
  ],
  [routes.faqs]: [],
  [routes.forgotPassword]: [resetPassword],
  [routes.howItWorks]: [],
  [routes.gathering]: [],
  [routes.gatherings]: [],
  [routes.interstitialThanksForSharing]: [],
  [routes.interstitialLogistics]: [],
  [routes.interstitialWelcome]: [],
  [routes.library]: [],
  [routes.manageBilling]: [],
  [routes.managePlan]: [],
  [routes.medicalReviewer]: [],
  [routes.onboardingRoot]: [],
  [routes.onDemandCoaching]: [],
  [routes.ourApproach]: [],
  [routes.partnerActivate]: [],
  [routes.partnerOnboardingInterstitialThanksForSharing]: [],
  [routes.partnerOnboardingQuestionEmail]: [],
  [routes.partnerOnboardingQuestionHowLong]: [],
  [routes.partnerOnboardingQuestionDueDate]: [],
  [routes.partnerOnboardingQuestionJourneyLooksLike]: [],
  [routes.partnerOnboardingQuestionMostChallenging]: [],
  [routes.partnerOnboardingQuestionName]: [],
  [routes.partnerOnboardingQuestionPhone]: [],
  [routes.partnerOnboardingQuestionProvidersSeen]: [],
  [routes.partnerOnboardingQuestionProviderSelect]: [],
  [routes.partnerOnboardingRoot]: [],
  [routes.partnerRoot]: [],
  [routes.partnerSlugRoot]: [],
  [routes.paymentIssue]: [],
  [routes.planPayment]: [],
  [routes.planSelect]: [],
  [routes.program]: [
    crewPage,
    dashboardPage,
    faqsPage,
    libraryPage,
    settingsPage,
    signInPage,
  ],
  [routes.questionBirthday]: [],
  [routes.questionClinicRefer]: [],
  [routes.questionClinicSatisfaction]: [],
  [routes.questionCoverage]: [],
  [routes.questionDueDate]: [],
  [routes.questionEmail]: [],
  [routes.questionEmployer]: [],
  [routes.questionHealthConditions]: [],
  [routes.questionHowAnxious]: [],
  [routes.questionHowDidYouHear]: [],
  [routes.questionHowGrateful]: [],
  [routes.questionHowLong]: [],
  [routes.questionHowOptimistic]: [],
  [routes.questionHowSupported]: [],
  [routes.questionJourneyLooksLike]: [],
  [routes.questionNextMilestone]: [],
  [routes.questionMedicalDiagnosis]: [],
  [routes.questionMoreTreatmentDetails]: [],
  [routes.questionMostChallenging]: [],
  [routes.questionName]: [],
  [routes.questionPhone]: [],
  [routes.questionPregnancyBackground]: [],
  [routes.questionProvidersSeen]: [],
  [routes.questionZip]: [],
  [routes.referAFriend]: [],
  [routes.resetPassword]: [],
  [routes.root]: [],
  [routes.settings]: [signInPage],
  [routes.setPassword]: [allSetPage],
  [routes.signIn]: [forgotPassword, signUpPage, setPasswordPage],
  [routes.topic]: [],
};

const emptyArray: Importer[] = [];

export const usePrefetch = (): void => {
  const hasChanged = useAppSelector((state) => state.gitSha.hasChanged);
  const { pathname } = useLocation();

  // Articles are nested under /library/:topic/:slug, so this checks for that
  const path = pathname.includes(`${leaves.library}/`)
    ? routes.article
    : (pathname as Routes);
  const importers = importersMap[path] ?? emptyArray;

  const prefetch = () => Promise.all(importers.map(initImport)).catch(noop);
  const pageLoadFn = hasChanged ? noop : prefetch;

  useOnPageLoad(pageLoadFn);
};
